import { useState } from "react";
import { MapPin, CheckCircle2, HelpCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const neighborhoods = [
  "Back Bay",
  "Beacon Hill",
  "South End",
  "Dorchester",
  "Jamaica Plain",
  "Charlestown",
  "South Boston",
  "West Roxbury",
  "Allston–Brighton",
  "Roslindale",
  "East Boston",
  "Hyde Park",
];

const towns = [
  "Cambridge",
  "Somerville",
  "Brookline",
  "Newton",
  "Quincy",
  "Medford",
  "Malden",
  "Waltham",
  "Watertown",
  "Wellesley",
  "Lexington",
  "Framingham",
];

const checkZip = (zip) => {
  if (!/^\d{5}$/.test(zip)) return null;
  if (/^02[1-4]/.test(zip))
    return {
      ok: true,
      msg: `${zip} is in our core Greater Boston service area.`,
    };
  if (/^02[0-7]/.test(zip))
    return {
      ok: true,
      msg: `${zip} is in our wider Eastern Massachusetts service area.`,
    };
  return {
    ok: false,
    msg: `${zip} looks outside our usual area — call us anyway, we occasionally travel for projects.`,
  };
};

export default function ServiceArea() {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    setResult(checkZip(zip.trim()));
  };

  return (
    <section id="service-area" className="bg-limestone py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold-deep">
            Where we work
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            Serving Greater Boston &amp; MetroWest
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div
              data-testid="zip-checker-card"
              className="flex h-full flex-col justify-between rounded-2xl bg-navy p-6 sm:p-8"
            >
              <div>
                <MapPin className="h-8 w-8 text-gold" />
                <h3 className="mt-4 font-display text-xl font-semibold text-white">
                  Check your zip code
                </h3>
                <p className="mt-2 text-sm text-white/70">
                  Enter your zip code for a quick coverage check.
                </p>
                <form onSubmit={submit} className="mt-5 flex gap-2">
                  <input
                    data-testid="zip-checker-input"
                    type="text"
                    inputMode="numeric"
                    maxLength="5"
                    placeholder="02108"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                    className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold-light focus:outline-none"
                  />
                  <button
                    data-testid="zip-checker-submit-btn"
                    type="submit"
                    className="rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-gold-light"
                  >
                    Check
                  </button>
                </form>
                {result && (
                  <p
                    data-testid="zip-checker-result"
                    className={`mt-4 flex items-start gap-2 rounded-lg bg-white/10 p-3 text-sm ${
                      result.ok ? "text-gold-light" : "text-amber-200"
                    }`}
                  >
                    {result.ok ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    {result.msg}
                  </p>
                )}
              </div>
              <p className="mt-8 border-t border-white/10 pt-4 text-xs text-white/50">
                Don't see your town? Call (508) 969-2104 — we'll tell you
                straight away if we can help.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="grid h-full grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Boston neighborhoods
                </p>
                <ul
                  data-testid="service-area-neighborhoods"
                  className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5"
                >
                  {neighborhoods.map((n) => (
                    <li key={n} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-deep" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  MetroWest &amp; nearby towns
                </p>
                <ul
                  data-testid="service-area-towns"
                  className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5"
                >
                  {towns.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-navy" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
